"use client";

import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState } from "react";

// Exchange Rate and Constants
const EXCHANGE_RATE = 159; // USD to JMD
const DUTY_RATE = 0.35; // 35% duty
const BASE_FREIGHT_RATES = {
  1: 750,
  2: 1500,
  3: 2250,
  4: 3000,
  5: 3750,
  6: 4500,
  7: 5250,
  8: 6000,
  9: 6750,
 10: 7500,
};
const EXTRA_POUND_COST = 250; // Cost for each additional pound after 10

// Zod Schema
const formSchema = z.object({
  weight: z
    .string()
    .transform((value) => parseFloat(value))
    .refine((value) => !isNaN(value) && value > 0, {
      message: "Weight must be a number greater than 0",
    }),
  value: z
    .string()
    .transform((value) => parseFloat(value))
    .refine((value) => !isNaN(value) && value > 0, {
      message: "Value must be a number greater than 0",
    }),
});

export default function PackageForm() {
  const [result, setResult] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      weight: "",
      value: "",
    },
  });

  const calculateFreightCharge = (weight) => {
    if (weight <= 10) {
      return BASE_FREIGHT_RATES[weight] || 0;
    }
    return BASE_FREIGHT_RATES[10] + (weight - 10) * EXTRA_POUND_COST;
  };

  const calculateDuties = (valueUSD) => {
    if (valueUSD > 100) {
      const valueJMD = valueUSD * EXCHANGE_RATE;
      return valueJMD * DUTY_RATE;
    }
    return 0; // No duties for items <= 100 USD
  };

  const onSubmit = ({ weight, value }) => {
    const freightCharge = calculateFreightCharge(weight);
    const dutyCharge = calculateDuties(value);
    const total = freightCharge + dutyCharge;

    setResult({
      freightCharge,
      dutyCharge,
      total,
    });
  };

  return (
    <div className="max-w-lg mx-auto p-4">
      <h2 className="text-2xl font-semibold mb-6">Shipping Cost Calculator</h2>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Weight Input */}
        <div>
          <label
            htmlFor="weight"
            className="block text-sm font-medium text-gray-700"
          >
            Package Weight (lbs)
          </label>
          <input
            type="number"
            id="weight"
            {...register("weight")}
            className="mt-2 p-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {errors.weight && (
            <span className="text-red-500 text-sm">
              {errors.weight.message}
            </span>
          )}
        </div>

        {/* Value Input */}
        <div>
          <label
            htmlFor="value"
            className="block text-sm font-medium text-gray-700"
          >
            Value of Product (USD)
          </label>
          <input
            type="number"
            id="value"
            {...register("value")}
            className="mt-2 p-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {errors.value && (
            <span className="text-red-500 text-sm">{errors.value.message}</span>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="w-full py-2 px-4 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
        >
          Calculate Cost
        </button>
      </form>

      {/* Display Results */}
      {result && (
        <div className="mt-8 p-4 border border-gray-300 rounded-md shadow">
          <h3 className="text-xl font-semibold mb-4">Cost Breakdown</h3>
          <p className="text-sm">
            <strong>Freight Charge:</strong> $
            {result.freightCharge.toLocaleString("en-US")} JMD
          </p>
          <p className="text-sm">
            <strong>Duty Charge:</strong> $
            {result.dutyCharge.toLocaleString("en-US")} JMD
          </p>
          <p className="text-lg font-bold mt-2">
            <strong>Total Cost:</strong> ${result.total.toLocaleString("en-US")}{" "}
            JMD
          </p>
        </div>
      )}
    </div>
  );
}
